import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

interface ResizableDivProps {
    children: React.ReactNode;
    className?: string;
    id?: string;
    initialWidth?: string;
    initialHeight?: string;
    minWidth?: string;
    minHeight?: string;
    maxWidth?: string;
    maxHeight?: string;
    resizable?: Partial<Record<'bottom' | 'left' | 'right' | 'top', boolean>>;
    onResize?: (width: string, height: string) => void;
    onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
}
type Direction = 'left' | 'right' | 'top' | 'bottom';

export const ResizableDiv: React.FC<ResizableDivProps> = ({
    children,
    className = '',
    id,
    initialWidth = '100%',
    initialHeight = '100%',
    minWidth,
    minHeight,
    maxWidth,
    maxHeight,
    resizable = {},
    onResize,
    onContextMenu,
}) => {
    const generatedId = useId();
    const containerId = id || generatedId;
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: initialWidth, height: initialHeight });
    const dimensionsRef = useRef(dimensions);
    const [geometry, setGeometry] = useState({ width: 0, height: 0, parentWidth: 0, parentHeight: 0 });
    const drag = useRef<{
        handle: HTMLDivElement;
        pointerId: number;
        direction: Direction;
        x: number;
        y: number;
        width: number;
        height: number;
        cursor: string;
        userSelect: string;
    } | null>(null);

    const finishDrag = useCallback(() => {
        const current = drag.current;
        if (!current) return;
        drag.current = null;
        document.body.style.cursor = current.cursor;
        document.body.style.userSelect = current.userSelect;
        if (current.handle.hasPointerCapture?.(current.pointerId))
            current.handle.releasePointerCapture(current.pointerId);
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const measure = () => {
            const rect = container.getBoundingClientRect();
            const parent = container.parentElement?.getBoundingClientRect();
            setGeometry({
                width: rect.width,
                height: rect.height,
                parentWidth: parent?.width || 0,
                parentHeight: parent?.height || 0,
            });
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        if (container.parentElement) observer.observe(container.parentElement);
        // The IDE hides panels with a class; stop an active drag when any ancestor is hidden.
        const visibility = new MutationObserver(() => {
            if (drag.current && !container.getClientRects().length) finishDrag();
        });
        for (let ancestor: HTMLElement | null = container; ancestor; ancestor = ancestor.parentElement) {
            visibility.observe(ancestor, { attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
        }
        window.addEventListener('blur', finishDrag);
        return () => {
            observer.disconnect();
            visibility.disconnect();
            window.removeEventListener('blur', finishDrag);
            finishDrag();
        };
    }, [finishDrag]);

    const pixels = (size: string | undefined, parent: number, fallback: number) => {
        if (!size) return fallback;
        const value = parseFloat(size);
        if (!Number.isFinite(value)) return fallback;
        return size.endsWith('%') ? (value / 100) * parent : value;
    };
    const metrics = (vertical: boolean) => {
        const parent = containerRef.current?.parentElement?.getBoundingClientRect();
        const extent =
            (vertical ? parent?.width : parent?.height) || (vertical ? geometry.parentWidth : geometry.parentHeight);
        const min = pixels(vertical ? minWidth : minHeight, extent, 50);
        const max = Math.max(min, pixels(vertical ? maxWidth : maxHeight, extent, extent || 1000));
        return { min, max, extent };
    };
    const commit = (vertical: boolean, value: number) => {
        const { min, max, extent } = metrics(vertical);
        const bounded = Math.max(min, Math.min(max, value));
        const original = vertical ? initialWidth : initialHeight;
        const formatted = original.endsWith('%') && extent > 0 ? `${(bounded / extent) * 100}%` : `${bounded}px`;
        const next = { ...dimensionsRef.current, [vertical ? 'width' : 'height']: formatted };
        dimensionsRef.current = next;
        setDimensions(next);
        onResize?.(next.width, next.height);
    };
    const reset = (vertical: boolean) => {
        const { extent } = metrics(vertical);
        commit(vertical, pixels(vertical ? initialWidth : initialHeight, extent, 0));
    };
    const renderHandle = (direction: Direction, position: string, vertical: boolean) => {
        if (!resizable[direction]) return null;
        const { min, max, extent } = metrics(vertical);
        const value = pixels(
            vertical ? dimensions.width : dimensions.height,
            extent,
            vertical ? geometry.width : geometry.height
        );
        return (
            <div
                role='separator'
                tabIndex={0}
                aria-label={`Resize ${direction} panel edge`}
                aria-controls={containerId}
                aria-orientation={vertical ? 'vertical' : 'horizontal'}
                aria-valuemin={Math.round(min)}
                aria-valuemax={Math.round(max)}
                aria-valuenow={Math.round(Math.max(min, Math.min(max, value)))}
                aria-valuetext={`${Math.round(value)} pixels`}
                className={`absolute ${position} z-10 group hover:bg-blue-500/30 focus-visible:bg-blue-500/30 ${vertical ? 'w-1 cursor-ew-resize' : 'h-1 cursor-ns-resize'}`}
                style={{ touchAction: 'none' }}
                onPointerDown={event => {
                    if (!event.isPrimary || event.button !== 0) return;
                    event.preventDefault();
                    event.stopPropagation();
                    finishDrag();
                    event.currentTarget.focus();
                    const rect = containerRef.current!.getBoundingClientRect();
                    drag.current = {
                        handle: event.currentTarget,
                        pointerId: event.pointerId,
                        direction,
                        x: event.clientX,
                        y: event.clientY,
                        width: rect.width,
                        height: rect.height,
                        cursor: document.body.style.cursor,
                        userSelect: document.body.style.userSelect,
                    };
                    event.currentTarget.setPointerCapture(event.pointerId);
                    document.body.style.cursor = vertical ? 'ew-resize' : 'ns-resize';
                    document.body.style.userSelect = 'none';
                }}
                onPointerMove={event => {
                    const current = drag.current;
                    if (!current || current.pointerId !== event.pointerId) return;
                    const delta = vertical ? event.clientX - current.x : event.clientY - current.y;
                    const sign = direction === 'left' || direction === 'top' ? -1 : 1;
                    commit(vertical, (vertical ? current.width : current.height) + delta * sign);
                }}
                onPointerUp={event => {
                    if (drag.current?.pointerId === event.pointerId) finishDrag();
                }}
                onPointerCancel={event => {
                    if (drag.current?.pointerId === event.pointerId) finishDrag();
                }}
                onLostPointerCapture={event => {
                    if (drag.current?.pointerId === event.pointerId) finishDrag();
                }}
                onDoubleClick={event => {
                    event.preventDefault();
                    reset(vertical);
                }}
                onKeyDown={event => {
                    const negative = vertical ? 'ArrowLeft' : 'ArrowUp';
                    const positive = vertical ? 'ArrowRight' : 'ArrowDown';
                    if (![negative, positive, 'Home', 'End', 'Enter'].includes(event.key)) return;
                    event.preventDefault();
                    finishDrag();
                    if (event.key === 'Enter') return reset(vertical);
                    const bounds = metrics(vertical);
                    const current = pixels(
                        vertical ? dimensionsRef.current.width : dimensionsRef.current.height,
                        bounds.extent,
                        0
                    );
                    commit(
                        vertical,
                        event.key === 'Home'
                            ? bounds.min
                            : event.key === 'End'
                              ? bounds.max
                              : current + (event.key === positive ? 1 : -1) * (event.shiftKey ? 50 : 10)
                    );
                }}
            >
                <div
                    aria-hidden='true'
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500 rounded-full opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 ${vertical ? 'w-1 h-5' : 'w-5 h-1'}`}
                />
            </div>
        );
    };
    return (
        <div
            ref={containerRef}
            id={containerId}
            className={`relative ${className}`}
            style={dimensions}
            onContextMenu={onContextMenu}
        >
            {children}
            {renderHandle('left', 'left-0 top-0 bottom-0', true)}
            {renderHandle('right', 'right-0 top-0 bottom-0', true)}
            {renderHandle('top', 'top-0 left-0 right-0', false)}
            {renderHandle('bottom', 'bottom-0 left-0 right-0', false)}
        </div>
    );
};
